'use client';

import React, { useState } from 'react';
import Image from 'next/image';

import RichText from '@/components/ui/RichText/RichText';
import {
  ClinicDoctifyWidget,
  ClinicTrustPilotWidget,
} from '@/components/widgets/ClinicDoctifyWidget';
import { FormSubmissionModal } from '@/components/forms/FormSubmissionModal/FormSubmissionModal';

import type { WhyChooseSectionProps } from './types';
import CheckCircleIcon from '@/components/ui/CheckCircleIcon';
import { getFormDefinition } from '@/lib/forms/formDefinitions';
import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import SecondaryButton from '@/components/ui/Button/SecondaryButton';

const defaultPatientExperienceImage = '/images/patients-experience.png';
const defaultTopDoctorsLogo = '/images/top-doctors-logo.png';

const WhyChooseSection: React.FC<WhyChooseSectionProps> = ({
  data,
  bookingFormId = 'booking-section',
  enquireFormId = '',
  wrapperClassName = '',
  onEnquireClick,
  enquireModalFormId,
  enquireModalMetadata,
  enquireModalTitle,
  enquireModalDescription,
  enquireModalSubmitLabel,
  noCtaBlur = false,
}) => {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  if (!data) return null;

  const features = data?.features || [];

  const enquiryModalConfig = enquireModalFormId
    ? getFormDefinition(enquireModalFormId)
    : undefined;

  const scrollToElement = (id?: string) => {
    if (!id) return;

    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleBookAppointment = () => {
    scrollToElement(bookingFormId);
  };

  const handleEnquire = () => {
    if (enquiryModalConfig) {
      setHasBeenOpened(true);
      setEnquiryOpen(true);
      return;
    }

    if (enquireFormId) {
      scrollToElement(enquireFormId);
      return;
    }

    onEnquireClick?.();
  };

  return (
    <section
      className={`content-wrapper bg-theme-brand-blue mx-auto w-full overflow-hidden rounded-[8px] py-14 lg:py-16 ${wrapperClassName}`}
    >
      <div>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_0.95fr] lg:items-center xl:gap-16">
          <div>
            {data?.heading?.content && (
              <RichText
                styles={{
                  h2: 'text-[38px] font-bold leading-[46px] text-white md:text-[44px] md:leading-[54px] lg:text-[48px] lg:leading-[58px]',
                  p: 'text-[38px] font-bold leading-[46px] text-white md:text-[44px] md:leading-[54px] lg:text-[48px] lg:leading-[58px]',
                  className: 'mb-10 max-w-140',
                  ...data.heading.styles,
                }}
              >
                {data.heading.content}
              </RichText>
            )}

            {features.length > 0 && (
              <div className="space-y-6">
                {features.map((feature, index) => (
                  <div
                    key={feature?.id ?? index}
                    className="flex items-start gap-4"
                  >
                    <CheckCircleIcon />

                    <div>
                      {feature?.title?.content && (
                        <RichText
                          styles={{
                            h3: 'text-[22px] font-bold leading-[30px] text-white md:text-[24px] md:leading-[32px]',
                            p: 'text-[22px] font-bold leading-[30px] text-white md:text-[24px] md:leading-[32px]',
                            ...feature.title.styles,
                          }}
                        >
                          {feature.title.content}
                        </RichText>
                      )}

                      {feature?.description?.content && (
                        <RichText
                          styles={{
                            p: 'text-[15px] font-normal leading-[26px] text-white/85',
                            className: 'mt-2 max-w-130',
                            ...feature.description.styles,
                          }}
                        >
                          {feature.description.content}
                        </RichText>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              className={`mt-12 max-w-[615px] rounded-[6px] ${noCtaBlur ? '' : 'border border-white/25 bg-white/10 px-6 py-5 shadow-sm backdrop-blur-sm'}`}
            >
              {data?.ctaHeading?.content && (
                <RichText
                  styles={{
                    h3: 'text-[28px] font-bold leading-[36px] text-white md:text-[30px] md:leading-[38px]',
                    p: 'text-[28px] font-bold leading-[36px] text-white md:text-[30px] md:leading-[38px]',
                    className: 'mb-4',
                    ...data.ctaHeading.styles,
                  }}
                >
                  {data.ctaHeading.content}
                </RichText>
              )}

              <div
                className={`grid grid-cols-1 gap-4 ${data?.bookButton?.content && data?.enquireButton?.content ? 'sm:grid-cols-2' : ''}`}
              >
                {data?.bookButton?.content && (
                  <PrimaryButton
                    data={{
                      title: data.bookButton.content,
                      url: data.bookButton.link || '#',
                      slug: data.bookButton.slug,
                      functionality: data.bookButton.functionality,
                      target: data.bookButton.target,
                    }}
                    onClick={handleBookAppointment}
                    className={`min-h-[61px] ${data?.enquireButton?.content ? 'w-full' : 'w-fit'} rounded-[4px] border-2 px-8 text-center text-[16px] font-normal ${
                      data.bookButton.variant === 'secondary'
                        ? 'text-theme-primary! border-white! bg-white! hover:border-white! hover:bg-transparent! hover:text-white!'
                        : 'border-theme-primary hover:border-theme-primary hover:border-2 hover:bg-white'
                    }`}
                  />
                )}

                {data?.enquireButton?.content && (
                  <SecondaryButton
                    data={{
                      title: data.enquireButton.content,
                      url: data.enquireButton.link || '#',
                      slug: data.enquireButton.slug,
                      functionality: data.enquireButton.functionality,
                      target: data.enquireButton.target,
                    }}
                    onClick={handleEnquire}
                    className="text-theme-primary min-h-[61px] w-full cursor-pointer rounded-[4px] border-white bg-white px-8 text-center text-[16px] font-normal hover:border-white hover:bg-transparent hover:text-white"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-x-6 lg:gap-y-8">
            <div className="flex min-h-[151px] items-center justify-center overflow-hidden rounded-[8px] shadow-md">
              <a
                href={data?.patientExperienceUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Patient Experience"
                className="relative flex h-auto w-full items-center justify-center md:h-[151px]"
              >
                <Image
                  src={
                    data?.patientExperienceImage ||
                    defaultPatientExperienceImage
                  }
                  alt="Patient Experience"
                  width={253}
                  height={151}
                  className="object-inherit h-full w-full"
                />
              </a>
            </div>

            <div className="flex min-h-[151px] items-center justify-center overflow-hidden rounded-[8px] bg-white shadow-md">
              <div className="flex h-[151px] w-full items-center justify-center overflow-hidden">
                <div className="ml-[-7px] flex h-[151px] w-full items-start justify-center overflow-hidden">
                  <ClinicDoctifyWidget
                    widgetId="why-choose-doctify-widget"
                    scale="scale-[1]"
                  />
                </div>
              </div>
            </div>

            <div className="flex min-h-[132px] items-center justify-center overflow-hidden rounded-[8px] bg-white shadow-md">
              <div className="mx-auto ml-[12px] flex w-full max-w-full justify-center">
                <ClinicTrustPilotWidget />
              </div>
            </div>

            <div className="flex min-h-[132px] items-center justify-center overflow-hidden rounded-[8px] px-6 py-5">
              <a
                href={data?.topDoctorsUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Top Doctors"
                className="flex h-full w-full items-center justify-center"
              >
                <Image
                  src={data?.topDoctorsLogo || defaultTopDoctorsLogo}
                  alt="Top Doctors"
                  width={175}
                  height={31}
                  sizes="160px"
                  className="h-auto max-h-[95px] w-full object-contain"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
      {enquiryModalConfig && hasBeenOpened && (
        <FormSubmissionModal
          open={enquiryOpen}
          onOpenChange={setEnquiryOpen}
          formId={enquiryModalConfig.formId}
          schema={enquiryModalConfig.schema}
          fields={enquiryModalConfig.fields}
          defaultValues={enquiryModalConfig.defaultValues}
          title={enquireModalTitle ?? 'Send us an enquiry'}
          description={
            enquireModalDescription ??
            'Fill in your details and our team will contact you.'
          }
          submitLabel={enquireModalSubmitLabel ?? 'Send enquiry'}
          metadata={enquireModalMetadata}
        />
      )}
    </section>
  );
};

export default WhyChooseSection;
